import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { firmMarkdownComponents, markdownComponents } from './markdown';
import { durationLabel } from './format';
import type { Message, MessageMeta } from './types';

/**
 * The conversation itself — bubbles, the thinking indicator, and the per-answer think-time
 * readout. Renders the messages only; the scroll container belongs to the surface, because
 * the page and the panel scroll differently (docs/24).
 */

const cls = {
  // Chat bubbles get a deliberately softer radius than the app's 2px token —
  // the familiar messaging shape reads as "conversation" at a glance.
  userMsg:  'ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-navy px-4 py-2.5 text-[13px] leading-relaxed text-cream whitespace-pre-wrap',
  botMsg:   'mr-auto max-w-[90%] min-w-0 rounded-2xl rounded-bl-md bg-cream px-4 py-2.5 text-[13px] leading-relaxed text-navy',
  errMsg:   'mr-auto max-w-[90%] rounded-2xl rounded-bl-md bg-danger/8 px-4 py-2.5 text-[13px] leading-relaxed text-danger',
  thinking: 'mr-auto flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-cream px-4 py-3',
  dot:      'h-1.5 w-1.5 rounded-full bg-bronze animate-bounce',
  thinkMeta:'ml-1.5 text-[11px] tabular-nums text-slate',
  answerMeta:'mr-auto mt-1 flex items-center gap-1.5 px-1 text-[10px] uppercase tracking-[0.06em] tabular-nums text-slate',
};

/** Shimmering "thinking" indicator shown while waiting on an answer. When `startedAt`
 *  is given it counts up live, showing how long the model has been thinking so far. */
export function ThinkingIndicator({ startedAt }: { startedAt?: number }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (startedAt == null) return;
    setElapsed(Date.now() - startedAt);
    const t = setInterval(() => setElapsed(Date.now() - startedAt), 100);
    return () => clearInterval(t);
  }, [startedAt]);
  return (
    <div className={cls.thinking} aria-live="polite" aria-label="The assistant is thinking">
      <span className={cls.dot} style={{ animationDelay: '0ms' }} />
      <span className={cls.dot} style={{ animationDelay: '150ms' }} />
      <span className={cls.dot} style={{ animationDelay: '300ms' }} />
      {startedAt != null && (
        <span className={cls.thinkMeta}>Thinking… {durationLabel(elapsed)}</span>
      )}
    </div>
  );
}

/** Small think-time readout shown beneath a fresh assistant answer. */
export function AnswerMeta({ meta }: { meta: MessageMeta }) {
  return (
    <div className={cls.answerMeta}>
      <span>Thought for {durationLabel(meta.thinkMs)}</span>
    </div>
  );
}

export function MessageList({ messages }: { messages: Message[] }) {
  return (
    <>
      {messages.map((m) => (
        <div key={m.id}>
          <div className={m.role === 'user' ? cls.userMsg : m.role === 'error' ? cls.errMsg : cls.botMsg}>
            {m.role === 'assistant'
              ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={m.firm ? firmMarkdownComponents : markdownComponents}>{m.text}</ReactMarkdown>
              : m.text}
          </div>
          {m.role === 'assistant' && m.meta && <AnswerMeta meta={m.meta} />}
        </div>
      ))}
    </>
  );
}
